import { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [heritageOpen, setHeritageOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/about", label: "About" },
    { href: "/services", label: "Services" },
    { 
      href: "/heritage", 
      label: "Heritage",
      submenu: [
        { href: "/heritage", label: "Overview" },
        { href: "/heritage/timeline", label: "Timeline" },
        { href: "/heritage/archive", label: "Archive" },
      ]
    },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-[#060B19]/95 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo"
            className="flex items-center"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_doc-breakdown/artifacts/t51v67ke_LighV.png"
              alt="Management To Learn USA LLC"
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <div key={link.href} className="relative group">
                {link.submenu ? (
                  <>
                    <button
                      className={`flex items-center gap-1 text-sm font-medium transition-colors relative ${
                        isActive(link.href) ? "text-primary" : "text-white/70 hover:text-primary"
                      }`}
                      onMouseEnter={() => setHeritageOpen(true)}
                      onMouseLeave={() => setHeritageOpen(false)}
                    >
                      {link.label}
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <AnimatePresence>
                      {heritageOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          onMouseEnter={() => setHeritageOpen(true)}
                          onMouseLeave={() => setHeritageOpen(false)}
                          className="absolute top-full left-0 mt-2 py-2 w-40 glass-card rounded-xl overflow-hidden"
                        >
                          {link.submenu.map((sublink) => (
                            <Link
                              key={sublink.href}
                              to={sublink.href}
                              className="block px-4 py-2 text-sm text-white/70 hover:text-primary hover:bg-white/5 transition-colors"
                            >
                              {sublink.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <Link
                    to={link.href}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                    className={`text-sm font-medium transition-colors relative group ${
                      isActive(link.href) ? "text-primary" : "text-white/70 hover:text-primary"
                    }`}
                  >
                    {link.label}
                    <span className={`absolute -bottom-1 left-0 h-0.5 bg-primary transition-all duration-300 ${
                      isActive(link.href) ? "w-full" : "w-0 group-hover:w-full"
                    }`} />
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/transition"
              className="text-sm text-secondary hover:underline"
            >
              What's New?
            </Link>
            <Link to="/contact">
              <Button
                data-testid="header-cta"
                className="bg-primary text-[#060B19] hover:bg-primary-hover rounded-lg px-6 py-2 font-semibold transition-all btn-glow"
              >
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              data-testid="mobile-menu"
              className="md:hidden glass-card rounded-2xl mb-4 overflow-hidden"
            >
              <nav className="flex flex-col py-4 px-6">
                {navLinks.map((link) => (
                  <div key={link.href}>
                    <Link
                      to={link.href}
                      data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                      className={`py-3 font-medium border-b border-white/10 last:border-0 transition-colors block ${
                        isActive(link.href) ? "text-primary" : "text-white/80 hover:text-primary"
                      }`}
                    >
                      {link.label}
                    </Link>
                    {link.submenu && (
                      <div className="pl-4 border-b border-white/10">
                        {link.submenu.map((sublink) => (
                          <Link
                            key={sublink.href}
                            to={sublink.href}
                            className="py-2 text-sm text-white/60 hover:text-primary block"
                          >
                            {sublink.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <Link to="/transition" className="py-3 text-secondary text-sm">
                  What's New?
                </Link>
                <Link to="/contact" className="mt-4">
                  <Button
                    data-testid="mobile-cta"
                    className="w-full bg-primary text-[#060B19] hover:bg-primary-hover rounded-lg"
                  >
                    Get Started
                  </Button>
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
